import {
  Pagination,
  PaginationDto,
  paginationFromDto,
} from '../common/models/pagination'
import { SiteID } from '../sites/models/site'
import {
  ShiftPreset,
  ShiftPresetID,
  ShiftPresetSchema,
} from './models/shiftPreset'

import axiosInstance from '../axiosInstance'

export interface ShiftPresetSearchForm {
  name?: string
  perPage: number
  page: number
}

interface ShiftPresetSearchParams {
  name?: string
  per_page: number
  page: number
}

export const fetch = async (
  siteId: SiteID,
  form: ShiftPresetSearchForm,
): Promise<{ shiftPresets: ShiftPreset[]; pagination: Pagination }> => {
  const params: ShiftPresetSearchParams = {
    per_page: form.perPage,
    page: form.page,
  }

  const name = form.name?.trim() ?? ''
  if (name.length > 0) {
    params.name = name
  }

  const response = await axiosInstance.get(`api/orgs/${siteId}/shift-presets`, { params })

  const data = response.data.data as unknown[]
  const meta = response.data.meta as { pagination: PaginationDto }

  return {
    shiftPresets: data.map((shiftPreset) => {
      return ShiftPresetSchema.parse(shiftPreset)
    }),
    pagination: paginationFromDto(meta.pagination),
  }
}

export interface ShiftPresetCreateForm {
  name: string
  colorHex: string
  startTime: string
  endTime: string
}

interface ShiftPresetCreateData {
  name: string
  color_hex: string
  start_time: string
  end_time: string
}

export const create = async (
  siteId: SiteID,
  form: ShiftPresetCreateForm,
): Promise<{ shiftPreset: ShiftPreset }> => {
  const data: ShiftPresetCreateData = {
    name: form.name,
    color_hex: form.colorHex,
    start_time: form.startTime,
    end_time: form.endTime,
  }

  const response = await axiosInstance.post(
    `api/orgs/${siteId}/shift-presets`,
    data,
  )

  const shiftPresetJson = response.data.data as unknown

  return {
    shiftPreset: ShiftPresetSchema.parse(shiftPresetJson),
  }
}

export interface ShiftPresetUpdateForm {
  name?: string
  colorHex?: string
  startTime?: string
  endTime?: string
}

interface ShiftPresetUpdateData {
  name?: string
  color_hex?: string
  start_time?: string
  end_time?: string
}

function updateFormToData(form: ShiftPresetUpdateForm): ShiftPresetUpdateData {
  const data: ShiftPresetUpdateData = {}

  const name = form.name?.trim()
  if (name) {
    data.name = name
  }

  const colorHex = form.colorHex?.trim()
  if (colorHex) {
    data.color_hex = colorHex
  }

  const startTime = form.startTime?.trim()
  if (startTime) {
    data.start_time = startTime
  }

  const endTime = form.endTime?.trim()
  if (endTime) {
    data.end_time = endTime
  }

  return data
}

export const update = async (
  siteId: SiteID,
  shiftPresetId: ShiftPresetID,
  form: ShiftPresetUpdateForm,
): Promise<{ shiftPreset: ShiftPreset }> => {
  const data = updateFormToData(form)

  const response = await axiosInstance.patch(
    `api/orgs/${siteId}/shift-presets/${shiftPresetId}`,
    data,
  )

  const shiftPresetJson = response.data.data as unknown

  return {
    shiftPreset: ShiftPresetSchema.parse(shiftPresetJson),
  }
}

export const destroy = async (
  siteId: SiteID,
  shiftPresetId: ShiftPresetID,
): Promise<void> => {
  await axiosInstance.delete(
    `api/orgs/${siteId}/shift-presets/${shiftPresetId}`,
  )
}
